import React, { createContext, useState, useContext, useCallback } from 'react';
import {
  getAllEquipment,
  getEquipmentById,
  createEquipment as createEquipmentService,
  updateEquipment as updateEquipmentService,
  deleteEquipment as deleteEquipmentService,
  assignEquipment as assignEquipmentService,
  returnEquipment as returnEquipmentService,
  getEquipmentByLocation as getEquipmentByLocationService,
} from '../services/equipmentService';

const EquipmentContext = createContext(null);

export const EquipmentProvider = ({ children }) => {
  const [equipment, setEquipment] = useState([]);
  const [equipmentByLocation, setEquipmentByLocation] = useState([]);
  const [selectedEquipment, setSelectedEquipmentState] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    available: 0,
    inUse: 0,
    maintenance: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    limit: 50, // Default limit
    page: 1,   // Default page
  });

  /**
   * Calculate equipment statistics
   * @param {Array} equipmentList - Array of equipment items
   */
  const calculateStats = useCallback((equipmentList) => {
    if (!Array.isArray(equipmentList)) return;

    const statsData = {
      total: equipmentList.length,
      // Use in_use field from API instead of status field
      available: equipmentList.filter((item) => item.in_use === false).length,
      inUse: equipmentList.filter((item) => item.in_use === true).length,
      maintenance: equipmentList.filter((item) => item.initial_status_id === 3).length, // Assuming 3 = maintenance
    };

    setStats(statsData);
  }, []);

  /**
   * Fetch all equipment with pagination
   * @param {number} limit - Number of items per page
   * @param {number} page - Current page number
   */
 const fetchAllEquipment = useCallback(async (limit = 50, page = 1) => {
  setLoading(true);
  setError(null);

  setPagination({ limit, page }); // Update pagination state

  try {
    const response = await getAllEquipment(limit, page);

    if (response.success) {
      // Append new equipment data to the existing data
      setEquipment((prevEquipment) => [
        ...prevEquipment,
        ...(response.data || []),
      ]);
      calculateStats(response.data || []);
    } else {
      setError(response.error);
      console.error('Failed to fetch equipment:', response.error);
    }
  } catch (error) {
    setError('Failed to fetch equipment');
    console.error('Error fetching equipment:', error);
  }

  setLoading(false);
  return;
}, [calculateStats]);

  /**
   * Fetch equipment by ID
   * @param {number|string} equipmentId - The equipment ID
   */
  const fetchEquipmentById = useCallback(async (equipmentId) => {
    setLoading(true);
    setError(null);

    const response = await getEquipmentById(equipmentId);

    if (response.success) {
      setSelectedEquipmentState(response.data);
    } else {
      setError(response.error);
      console.error('Failed to fetch equipment details:', response.error);
    }

    setLoading(false);
    return response;
  }, []);

  /**
   * Create new equipment
   * @param {FormData} formData - Equipment data including optional file upload
   */
  const createEquipment = useCallback(async (formData) => {
    setLoading(true);
    setError(null);

    const response = await createEquipmentService(formData);

    if (response.success) {
      // Optimistic update - add to local state
      setEquipment((prev) => [...prev, response.data]);
      calculateStats([...equipment, response.data]);
    } else {
      setError(response.error);
      console.error('Failed to create equipment:', response.error);
    }

    setLoading(false);
    return response;
  }, [equipment, calculateStats]);

  /**
   * Update existing equipment
   * @param {number|string} equipmentId - The equipment ID
   * @param {FormData|Object} data - Equipment update data
   */
  const updateEquipment = useCallback(async (equipmentId, data) => {
    setLoading(true);
    setError(null);

    const response = await updateEquipmentService(equipmentId, data);

    if (response.success) {
      // Optimistic update - update in local state
      setEquipment((prev) =>
        prev.map((item) =>
          item.equipment_id === equipmentId ? { ...item, ...response.data } : item
        )
      );

      // Update selected equipment if it's the one being updated
      if (selectedEquipment?.equipment_id === equipmentId) {
        setSelectedEquipmentState((prev) => ({ ...prev, ...response.data }));
      }

      // Recalculate stats
      const updatedList = equipment.map((item) =>
        item.equipment_id === equipmentId ? { ...item, ...response.data } : item
      );
      calculateStats(updatedList);
    } else {
      setError(response.error);
      console.error('Failed to update equipment:', response.error);
    }

    setLoading(false);
    return response;
  }, [equipment, selectedEquipment, calculateStats]);

  /**
   * Delete equipment
   * @param {number|string} equipmentId - The equipment ID
   */
  const deleteEquipment = useCallback(async (equipmentId) => {
    setLoading(true);
    setError(null);

    const response = await deleteEquipmentService(equipmentId);

    if (response.success) {
      // Optimistic update - remove from local state
      setEquipment((prev) => {
        const updatedList = prev.filter((item) => item.equipment_id !== equipmentId);
        calculateStats(updatedList);
        return updatedList;
      });

      // Clear selected equipment if it's the one being deleted
      if (selectedEquipment?.equipment_id === equipmentId) {
        setSelectedEquipmentState(null);
      }
    } else {
      setError(response.error);
      console.error('Failed to delete equipment:', response.error);
    }

    setLoading(false);
    return response;
  }, [selectedEquipment, calculateStats]);

  /**
   * Assign equipment to crew/project
   * @param {Object} assignmentData - Assignment details
   */
  const assignEquipment = useCallback(async (assignmentData) => {
    setLoading(true);
    setError(null);

    const response = await assignEquipmentService(assignmentData);

    if (response.success) {
      // Refresh equipment list to get updated assignment status
      await fetchAllEquipment();
    } else {
      setError(response.error);
      console.error('Failed to assign equipment:', response.error);
    }

    setLoading(false);
    return response;
  }, [fetchAllEquipment]);

  /**
   * Return equipment from assignment
   * @param {Object} returnData - Return details
   */
  const returnEquipment = useCallback(async (returnData) => {
    setLoading(true);
    setError(null);

    const response = await returnEquipmentService(returnData);

    if (response.success) {
      // Refresh equipment list to get updated assignment status
      await fetchAllEquipment();
    } else {
      setError(response.error);
      console.error('Failed to return equipment:', response.error);
    }

    setLoading(false);
    return response;
  }, [fetchAllEquipment]);

  /**
   * Fetch equipment grouped by location
   */
  const fetchByLocation = useCallback(async () => {
    setLoading(true);
    setError(null);

    const response = await getEquipmentByLocationService();

    if (response.success) {
      setEquipmentByLocation(response.data || []);
    } else {
      setError(response.error);
      console.error('Failed to fetch equipment by location:', response.error);
    }

    setLoading(false);
    return response;
  }, []);

  /**
   * Set selected equipment (for detail views)
   * @param {Object} equipmentItem - The equipment object
   */
  const setSelectedEquipment = useCallback((equipmentItem) => {
    setSelectedEquipmentState(equipmentItem);
  }, []);

  /**
   * Clear selected equipment
   */
  const clearSelectedEquipment = useCallback(() => {
    setSelectedEquipmentState(null);
  }, []);

  const value = {
    // State
    equipment,
    equipmentByLocation,
    selectedEquipment,
    stats,
    loading,
    error,
    pagination,  // Expose pagination state

    // Methods
    fetchAllEquipment,
    fetchEquipmentById,
    createEquipment,
    updateEquipment,
    deleteEquipment,
    assignEquipment,
    returnEquipment,
    fetchByLocation,
    setSelectedEquipment,
    clearSelectedEquipment,
  };

  return (
    <EquipmentContext.Provider value={value}>
      {children}
    </EquipmentContext.Provider>
  );
};

export const useEquipment = () => {
  const context = useContext(EquipmentContext);
  if (!context) {
    throw new Error('useEquipment must be used within EquipmentProvider');
  }
  return context;
};
